import { Row, Col, Modal, Button, Icon, Table as AntdTable, Tabs, Form, Input, Select } from 'antd';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import TableColumn from '../../../../models/Table/TableColumn';
import { FormComponentProps } from 'antd/lib/form/Form';
import ActiveIcon from '../../../../models/Table/Render/ActiveIcon';
import {
  fetchMemos,
  fetchAbsence,
  clearCustomerData,
  postAbsence,
  fetchMyCustomers
} from '../../../../core-module/redux/Customer/actions';
import { TitledContainer } from '../../../../components/TitledContainer/TitledContainer';
import { hasRole, RoleTypes } from '../../../../core-module/helpers/RoleHelper';
import { AppState } from '../../../../core-module/redux/reducers';
import { OrderTab } from './Orders/OrderTab';
import { ProfileTab } from './Profile/ProfileTab';
import Concat from '../../../../models/Table/Render/Concat';

const { TabPane } = Tabs;
const { TextArea } = Input;

interface OwnProps extends FormComponentProps {
  dispatch: Dispatch;
}

type Props = OwnProps & FormComponentProps & ReturnType<typeof mapStateToProps>;

interface State {
  visible: boolean;
  customer: any;
  showAbsenceForm: boolean;
  absenceData: any;
  activeTab: string;
  portion: any;
}

class Customers extends React.Component<Props, State> {
  private readonly columns: TableColumn[];
  private readonly memoColumns: TableColumn[];
  private readonly absenceColumns: TableColumn[];

  routeFilter: any;
  private timeout;

  constructor(props: Props) {
    super(props);

    this.state = {
      visible: false,
      customer: null,
      showAbsenceForm: false,
      absenceData: [],
      activeTab: '1',
      portion: undefined
    };

    this.timeout = null;

    this.columns = [
      new TableColumn('Voornaam', 'first_name', 'first_name').setAdditional({
        sorter: (a, b) => (a.first_name.toLowerCase() > b.first_name.toLowerCase() ? 1 : -1)
      }),
      new TableColumn('Achternaam', '', '')
        .setAdditional({
          sorter: (a, b) => (a.last_name.toLowerCase() > b.last_name.toLowerCase() ? 1 : -1),
          defaultSortOrder: 'ascend'
        })
        .setRender((rowData) => {
          return rowData.prefix ? rowData.prefix + ' ' + rowData.last_name : rowData.last_name;
        }),
      new TableColumn('Postcode', 'postal_code', 'postal_code').setAdditional({
        sorter: (a, b) => a.postal_code.toLowerCase() > b.postal_code.toLowerCase()
      }),
      new TableColumn('Adres', '', 'address')
        .setRender(new Concat({ keys: ['street_name', 'door_number'] }).render)
        .setAdditional({
          sorter: (a, b) => (a.street_name.toLowerCase() > b.street_name.toLowerCase() ? 1 : -1)
        }),
      new TableColumn('Klantnummer', 'customer_id', 'customer_id').setAdditional({
        sorter: (a, b) => (a.customer_id > b.customer_id ? 1 : -1)
      }),
      new TableColumn('Acties', '', 'actions')
        .setAdditional({ width: '100px' })
        .setRender((rowData) => {
          return (
            <Button
              style={{ marginRight: 5, top: '-1px' }}
              onClick={() => this.initModal(rowData)}
              type='primary'
              htmlType='button'
            >
              <Icon type='eye' />
            </Button>
          );
        })
    ];

    this.memoColumns = [
      new TableColumn('Beschrijving', 'tekst', 'tekst').setAdditional({ width: '50%' }),
      new TableColumn('Aangemaakt door', 'createdBy', 'createdBy'),
      new TableColumn('Aangemaakt op', 'createdOn', 'createdOn').setRender((rowData) => {
        return moment(rowData).format('dd DD MMM YYYY HH:mm');
      })
    ];

    this.absenceColumns = [
      new TableColumn('Week', 'weekNummerText', 'weekNummerText'),
      new TableColumn('Afgegeven', 'afgegeven', 'afgegeven').setRender((rowData) => {
        return new ActiveIcon().render(rowData);
      }),
      new TableColumn('Afwezig', 'afwezig', 'afwezig').setRender((rowData) => {
        return new ActiveIcon().render(rowData);
      }),
      new TableColumn('Ingeleverd', 'ingeleverd', 'ingeleverd').setRender((rowData) => {
        return new ActiveIcon().render(rowData);
      }),
      new TableColumn('Opmerkingen', 'opmerking', 'opmerking'),
      new TableColumn('Acties', '', 'actions')
        .setAdditional({ width: '100px' })
        .setRender((rowData) => {
          return (
            <Button
              style={{ marginRight: 5, top: '-1px' }}
              onClick={() => this.showAbsenceForm(rowData)}
              type='primary'
              htmlType='button'
            >
              <Icon type='edit' />
            </Button>
          );
        })
    ];

    const driver = this.props.auth.user.driver;
    const start: string = moment().subtract(1, 'month').format('YYYY-MM-DD');
    const end: string = moment().add(2, 'days').format('YYYY-MM-DD');
    let isDepotAdmin =
      hasRole(this.props.auth.roles, RoleTypes.DEPOT_ADMIN) ||
      hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN);

    this.routeFilter = {
      filter: {
        include: [
          {
            relation: 'orders',
            scope: {
              fields: ['id', 'customer_id', 'postal_code', 'door_number', 'street_name', 'city'],
              include: [
                {
                  relation: 'customer',
                  scope: {
                    fields: [
                      'first_name',
                      'last_name',
                      'prefix',
                      'customer_id',
                      'camasys_id',
                      'postal_code',
                      'door_number',
                      'street_name',
                      'city'
                    ]
                  }
                }
              ]
            }
          }
        ],
        where: {
          and: [{ actual_date_driven: { between: [start, end] } }]
        },
        fields: ['id', 'date_driven', 'actual_date_driven', 'driver_id'],
        order: 'actual_date_driven DESC'
      }
    };

    isDepotAdmin
      ? this.routeFilter.filter.where.and.push({ depot_id: driver!.depot_id })
      : this.routeFilter.filter.where.and.push({ driver_id: driver!.id });

    this.onTabChange = this.onTabChange.bind(this);
  }

  componentDidMount() {
    document.title = 'Logistics | Klanten';
    this.props.dispatch(fetchMyCustomers(this.routeFilter));
  }

  initModal(rowData) {
    this.props.dispatch(clearCustomerData());
    this.setState({ customer: rowData, visible: true }, () =>
      this.onTabChange(this.state.activeTab)
    );
  }

  getMemo(customerId: number) {
    this.props.dispatch(fetchMemos({ id: customerId }));
  }

  getAbsence(customerId: number) {
    this.props.dispatch(fetchAbsence({ id: customerId }));
  }

  onTabChange = (tabIndex: string) => {
    switch (tabIndex) {
      case '1':
        break;
      case '2':
        break;
      case '3':
        if (this.props.customer.memos.length === 0) {
          this.getMemo(this.state.customer.id);
        }
        break;
      case '4':
        if (this.props.customer.absence.length === 0) {
          this.getAbsence(this.state.customer.id);
        }
        break;
    }

    if (tabIndex !== this.state.activeTab) {
      this.setState({ activeTab: tabIndex });
    }
  };

  showAbsenceForm = (rowData) => {
    this.setState({ showAbsenceForm: true, absenceData: rowData });
  };

  hideAbsenceForm = () => {
    this.setState({ showAbsenceForm: false, absenceData: [] });
  };

  saveAbsenceForm = () => {
    this.props.form.validateFields(
      ['absent', 'handed_in', 'handed_out', 'remarks'],
      (errors, values) => {
        if (!errors) {
          let data = { ...this.state.absenceData };
          data.afwezig = values.absent === 'true';
          data.ingeleverd = values.handed_in === 'true';
          data.afgegeven = values.handed_out === 'true';
          data.opmerking = values.remarks;
          this.setState({ showAbsenceForm: false });

          this.props.dispatch(postAbsence({ id: this.state.customer.id, values: data }));
        }
      }
    );
  };

  onChange = (value) => {
    clearTimeout(this.timeout);
    const searchValue = value.target.getAttribute('value');
    const search = this.onSearch;

    // Make a new timeout set to go off in 800ms
    this.timeout = setTimeout(function () {
      search(searchValue);
    }, 400);
  };

  onSearch = (value: string) => {
    let searchFields = [
      'first_name',
      'last_name',
      'customer_id',
      'postal_code',
      'door_number',
      'street_name',
      'city'
    ];
    let searchFilters: any = null;

    if (value) {
      const clauses: Array<any> = [];
      const fields = searchFields.filter((item) => item.includes('.') === false);
      for (let i = 0; i < fields.length; i++) {
        clauses.push({ [fields[i]]: { like: `%25${value}%25` } });
      }

      searchFilters = {
        filter: {
          where: {
            or: clauses
          }
        }
      };
    }

    let theFilters: any = JSON.parse(JSON.stringify(this.routeFilter)); // ...spread and obj.assign mutated the props.filters
    if (searchFilters) {
      theFilters.filter.where = { ...theFilters.filter.where, ...searchFilters.filter.where };
    }

    this.props.dispatch(fetchMyCustomers(theFilters));
  };

  render() {
    const columns = this.columns.map((column: TableColumn) => column.get());

    const memoColumns = this.memoColumns.map((column: TableColumn) => column.get());
    const absenceColumns = this.absenceColumns.map((column: TableColumn) => column.get());

    const { getFieldDecorator } = this.props.form;

    const formItemLayout = {
      style: { marginTop: 20 },
      labelCol: {
        xs: { span: 24 },
        sm: { span: 4 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 10 }
      }
    };

    return (
      <>
        <Row gutter={12}>
          <Col xs={24}>
            <TitledContainer title={`Klanten`}>
              <>
                <Row style={{ marginBottom: 10 }}>
                  <Col xs={24} sm={18}>
                    <em style={{ color: '#aaa' }}>Klanten van afgelopen 4 weken op jouw route</em>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Input
                      placeholder='Zoek in tabel'
                      onKeyUp={this.onChange}
                      suffix={<Icon type='search' />}
                    />
                  </Col>
                </Row>
                <AntdTable
                  rowKey={(row, index) => index.toString()}
                  rowClassName={() => 'striped-table-row'}
                  bordered={true}
                  size='small'
                  loading={this.props.customer.myCustomersLoading}
                  columns={columns}
                  dataSource={this.props.customer.myCustomers}
                  pagination={false}
                  locale={{ emptyText: 'Geen data beschikbaar' }}
                />
              </>
            </TitledContainer>
          </Col>
        </Row>

        {this.state.visible && (
          <Modal
            width='60%'
            title={
              this.state.customer
                ? `Klant ${this.state.customer.customer_id} | ${this.state.customer.first_name} ${
                    this.state.customer.prefix || ''
                  } ${this.state.customer.last_name}`
                : ''
            }
            visible={this.state.visible}
            onCancel={() => this.setState({ visible: false })}
            footer={[
              <Button
                key='cancel'
                htmlType='button'
                onClick={() => this.setState({ visible: false })}
              >
                Sluiten
              </Button>
            ]}
          >
            <Tabs defaultActiveKey='1' animated={false} onChange={this.onTabChange}>
              <TabPane tab='Profiel' key='1'>
                {this.state.customer && <ProfileTab customer={this.state.customer} />}
              </TabPane>
              <TabPane tab='Bestellingen' key='2'>
                {this.state.customer && this.state.customer.postal_code && (
                  <OrderTab customerId={this.state.customer.id} />
                )}
              </TabPane>
              <TabPane tab='Memo' key='3'>
                <AntdTable
                  rowKey={(row, index) => index.toString()}
                  rowClassName={() => 'striped-table-row'}
                  bordered={true}
                  size='small'
                  loading={this.props.customer.memosLoading}
                  columns={memoColumns}
                  dataSource={this.props.customer.memos}
                  pagination={false}
                  locale={{ emptyText: 'Geen data beschikbaar' }}
                />
              </TabPane>
              <TabPane tab='Afwezigheid' key='4'>
                {this.state.showAbsenceForm && (
                  <Form {...formItemLayout}>
                    <Form.Item label='Week'>
                      {getFieldDecorator('week', {
                        rules: [{ required: true, message: 'Week is onbekend' }],
                        initialValue: this.state.absenceData.weekNummerText
                      })(<Input type='text' readOnly={true} />)}
                    </Form.Item>
                    <Form.Item label='Afgegeven'>
                      {getFieldDecorator('handed_out', {
                        rules: [{ required: true, message: 'Maak een keuze' }],
                        initialValue: this.state.absenceData.afgegeven ? 'true' : 'false'
                      })(
                        <Select>
                          <Select.Option value='true'>Ja</Select.Option>
                          <Select.Option value='false'>Nee</Select.Option>
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item label='Afwezig'>
                      {getFieldDecorator('absent', {
                        rules: [{ required: true, message: 'Maak een keuze' }],
                        initialValue: this.state.absenceData.afwezig ? 'true' : 'false'
                      })(
                        <Select>
                          <Select.Option value='true'>Ja</Select.Option>
                          <Select.Option value='false'>Nee</Select.Option>
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item label='Ingeleverd'>
                      {getFieldDecorator('handed_in', {
                        rules: [{ required: true, message: 'Maak een keuze' }],
                        initialValue: this.state.absenceData.ingeleverd ? 'true' : 'false'
                      })(
                        <Select>
                          <Select.Option value='true'>Ja</Select.Option>
                          <Select.Option value='false'>Nee</Select.Option>
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item label='Opmerkingen' extra='Er zijn maximaal 400 tekens in te voeren'>
                      {getFieldDecorator('remarks', {
                        rules: [{ required: false }],
                        initialValue: this.state.absenceData.opmerking
                      })(<TextArea rows={4} />)}
                    </Form.Item>
                    <Row>
                      <Col xs={{ offset: 4, span: 10 }}>
                        <Button style={{ marginRight: 5 }} onClick={this.hideAbsenceForm}>
                          Annuleren
                        </Button>
                        <Button type='primary' onClick={this.saveAbsenceForm}>
                          Opslaan
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}

                {this.state.showAbsenceForm === false && (
                  <AntdTable
                    rowKey={(row, index) => index.toString()}
                    rowClassName={() => 'striped-table-row'}
                    bordered={true}
                    size='small'
                    loading={this.props.customer.absenceLoading}
                    columns={absenceColumns}
                    dataSource={this.props.customer.absence.reverse()}
                    pagination={false}
                    locale={{ emptyText: 'Geen data beschikbaar' }}
                  />
                )}
              </TabPane>
            </Tabs>
          </Modal>
        )}
      </>
    );
  }
}

const mapStateToProps = ({ customer, auth, table }: AppState) => ({
  customer,
  auth,
  table
});

export default connect(mapStateToProps)(Form.create()(Customers));
